// @ts-strict-ignore
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Block } from '@actual-app/components/block';
import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { InitialFocus } from '@actual-app/components/initial-focus';
import { styles } from '@actual-app/components/styles';
import { View } from '@actual-app/components/view';

import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import { useModalEnHoja } from '#components/common/modalesEnHoja';
import { ConfirmarHoja } from '#components/mobile/ui/ConfirmarHoja';
import type { Modal as ModalType } from '#modals/modalsSlice';

import { textosCuadrado } from './confirmarCuadrado';

type ConfirmTransactionEditModalProps = Extract<
  ModalType,
  { name: 'confirm-transaction-edit' }
>['options'];

export function ConfirmTransactionEditModal({
  onCancel,
  onConfirm,
  confirmReason,
}: ConfirmTransactionEditModalProps) {
  const { t } = useTranslation();

  const { isNarrowWidth } = useResponsive();
  const narrowButtonStyle = isNarrowWidth
    ? {
        height: styles.mobileMinHeight,
      }
    : {};

  const enHoja = useModalEnHoja('confirm-transaction-edit');
  // Textos de la hoja (B6 · H73): claves fijas para i18n.
  const cuadrado = textosCuadrado(confirmReason);

  const motivo =
    confirmReason === 'batchDeleteWithReconciledTransfer' ? (
      <Trans>
        This transfer has a linked transaction in another account that is
        reconciled. Deleting it may bring that account's reconciliation out of
        balance.
      </Trans>
    ) : confirmReason === 'batchDeleteWithReconciled' ? (
      <Trans>
        Deleting reconciled transactions may bring your reconciliation out of
        balance.
      </Trans>
    ) : confirmReason === 'batchEditWithReconciledTransfer' ? (
      <Trans>
        This transfer has a linked transaction in another account that is
        reconciled. Editing it may bring that account's reconciliation out of
        balance.
      </Trans>
    ) : confirmReason === 'batchEditWithReconciled' ? (
      <Trans>
        Editing reconciled transactions may bring your reconciliation out of
        balance.
      </Trans>
    ) : confirmReason === 'editReconciled' ? (
      <Trans>
        Saving your changes to this reconciled transaction may bring your
        reconciliation out of balance.
      </Trans>
    ) : confirmReason === 'unlockReconciled' ? (
      <Trans>
        Unlocking this transaction means you won't be warned about changes that
        can impact your reconciled balance. (Changes to amount, account, payee,
        etc).
      </Trans>
    ) : confirmReason === 'deleteReconciled' ? (
      <Trans>
        Deleting reconciled transactions may bring your reconciliation out of
        balance.
      </Trans>
    ) : (
      <Trans>Are you sure you want to edit this transaction?</Trans>
    );

  return (
    <Modal
      name="confirm-transaction-edit"
      containerProps={{ style: { width: '30vw' } }}
    >
      {({ state }) =>
        enHoja ? (
          <ConfirmarHoja
            icono={cuadrado.borra ? 'trash' : 'alert'}
            pregunta={t(cuadrado.pregunta)}
            detalle={t(cuadrado.detalle)}
            textoAccion={t(cuadrado.accion)}
            onConfirmar={() => {
              state.close();
              onConfirm();
            }}
            onCancelar={() => {
              state.close();
              onCancel?.();
            }}
            onCerrar={() => {
              state.close();
              onCancel?.();
            }}
          />
        ) : (
          <>
            <ModalHeader
              title={t('Reconciled Transaction')}
              rightContent={<ModalCloseButton onPress={() => state.close()} />}
            />
            <View style={{ lineHeight: 1.5 }}>
              <Block>{motivo}</Block>
              <View
                style={{
                  marginTop: 20,
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  aria-label={t('Cancel')}
                  style={{
                    marginRight: 10,
                    ...narrowButtonStyle,
                  }}
                  onPress={() => {
                    state.close();
                    onCancel();
                  }}
                >
                  <Trans>Cancel</Trans>
                </Button>
                <InitialFocus>
                  <Button
                    aria-label={t('Confirm')}
                    variant="primary"
                    style={{
                      marginRight: 10,
                      ...narrowButtonStyle,
                    }}
                    onPress={() => {
                      state.close();
                      onConfirm();
                    }}
                  >
                    <Trans>Confirm</Trans>
                  </Button>
                </InitialFocus>
              </View>
            </View>
          </>
        )
      }
    </Modal>
  );
}
